import {
    ProjectDto,
    type ProjectKpiDto,
    type UpdateMilestoneDto,
    type ProjectWorkspaceFileDto,
    type ProjectWorkspaceFileActivityDto,
    type ProjectCommentDto,
    type ProjectCommentParticipantDto,
    type ProjectActivityEventDto,
    type CreateProjectCommentDto,
    type ProjectCommentTargetKind,
    type ProjectCommentSummariesDto,
} from "@paladin-monorepo/common";
import API from "../api";

export const projectsApi = {
    async list(): Promise<ProjectDto[]> {
        return API.get<ProjectDto[]>('/projects').then((res) => res.data);
    },
    async getById(id: string): Promise<ProjectDto> {
        return API.get<ProjectDto>(`/projects/${encodeURIComponent(id)}`).then((res) => res.data);
    },
    async startProject(projectId: string): Promise<void> {
        await API.post(`/projects/${encodeURIComponent(projectId)}/start`);
    },
    async updateMilestone(
        projectId: string,
        milestoneId: string,
        payload: UpdateMilestoneDto,
    ): Promise<void> {
        await API.patch(
            `/projects/${encodeURIComponent(projectId)}/milestones/${encodeURIComponent(milestoneId)}`,
            payload,
        );
    },
    async updateProjectKpis(
        projectId: string,
        kpis: { description: string; value?: number; unit?: string }[],
    ): Promise<ProjectKpiDto[]> {
        const { data } = await API.put<ProjectKpiDto[]>(
            `/projects/${encodeURIComponent(projectId)}/kpis`,
            { kpis },
        );
        return data;
    },

    async listWorkspaceFiles(projectId: string, category?: string): Promise<ProjectWorkspaceFileDto[]> {
        const q = category ? `?category=${encodeURIComponent(category)}` : "";
        const { data } = await API.get<ProjectWorkspaceFileDto[]>(
            `/projects/${encodeURIComponent(projectId)}/workspace-files${q}`,
        );
        return data;
    },

    async getWorkspaceFilesActivity(projectId: string): Promise<ProjectWorkspaceFileActivityDto[]> {
        const { data } = await API.get<ProjectWorkspaceFileActivityDto[]>(
            `/projects/${encodeURIComponent(projectId)}/workspace-files/activity`,
        );
        return data;
    },

    async uploadWorkspaceFile(projectId: string, file: File, category: string): Promise<ProjectWorkspaceFileDto> {
        const form = new FormData();
        form.append("file", file);
        form.append("category", category);
        const { data } = await API.post<ProjectWorkspaceFileDto>(
            `/projects/${encodeURIComponent(projectId)}/workspace-files`,
            form,
        );
        return data;
    },

    async downloadWorkspaceFile(projectId: string, fileId: string, originalName: string): Promise<void> {
        const res = await API.get<Blob>(
            `/projects/${encodeURIComponent(projectId)}/workspace-files/${encodeURIComponent(fileId)}/download`,
            { responseType: "blob" },
        );
        const url = window.URL.createObjectURL(res.data);
        const a = document.createElement("a");
        a.href = url;
        a.download = originalName;
        a.click();
        window.URL.revokeObjectURL(url);
    },

    async deleteWorkspaceFile(projectId: string, fileId: string): Promise<void> {
        await API.delete(
            `/projects/${encodeURIComponent(projectId)}/workspace-files/${encodeURIComponent(fileId)}`,
        );
    },

    async getProjectActivity(projectId: string): Promise<ProjectActivityEventDto[]> {
        const { data } = await API.get<ProjectActivityEventDto[]>(
            `/projects/${encodeURIComponent(projectId)}/activity`,
        );
        return data;
    },

    async listCommentParticipants(projectId: string): Promise<ProjectCommentParticipantDto[]> {
        const { data } = await API.get<ProjectCommentParticipantDto[]>(
            `/projects/${encodeURIComponent(projectId)}/comment-participants`,
        );
        return data;
    },

    async getCommentSummaries(projectId: string): Promise<ProjectCommentSummariesDto> {
        const { data } = await API.get<ProjectCommentSummariesDto>(
            `/projects/${encodeURIComponent(projectId)}/comment-summaries`,
        );
        return data;
    },

    async listComments(
        projectId: string,
        targetKind: ProjectCommentTargetKind,
        targetId: string,
    ): Promise<ProjectCommentDto[]> {
        const q = new URLSearchParams({ targetKind, targetId });
        const { data } = await API.get<ProjectCommentDto[]>(
            `/projects/${encodeURIComponent(projectId)}/comments?${q.toString()}`,
        );
        return data;
    },

    async createComment(projectId: string, payload: CreateProjectCommentDto): Promise<ProjectCommentDto> {
        const { data } = await API.post<ProjectCommentDto>(
            `/projects/${encodeURIComponent(projectId)}/comments`,
            payload,
        );
        return data;
    },

    async deleteComment(projectId: string, commentId: string): Promise<void> {
        await API.delete(
            `/projects/${encodeURIComponent(projectId)}/comments/${encodeURIComponent(commentId)}`,
        );
    },
}
